"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var hotkey_1 = require("../hotkey/hotkey");
// Actions have one free argument; when a context is pushed onto the stack, that
// argument is stored along with it. An ActionInContext closes over the argument
// so that the action can be called anywhere
var ActionInContext = /** @class */ (function () {
    function ActionInContext(action, contextName, argument, element, service) {
        this.action = action;
        this.contextName = contextName;
        this.argument = argument;
        this.element = element;
        this.service = service;
    }
    Object.defineProperty(ActionInContext.prototype, "hasArgument", {
        get: function () {
            return !!this.argument;
        },
        enumerable: true,
        configurable: true
    });
    ActionInContext.prototype.act = function (e) {
        return this.action.actOn(this.argument, this.element, e);
    };
    ActionInContext.prototype.context = function () {
        this.service.contexts[this.contextName];
    };
    return ActionInContext;
}());
exports.ActionInContext = ActionInContext;
// An action is anything you can do in a given context. It can be mapped to a
// keyboard shortcut.
var Action = /** @class */ (function () {
    function Action(description) {
        this.remappedKey = null;
        this.remappedKey = null;
        this.name = description.name;
        this.shortDocumentation = description.shortDocumentation;
        this.searchTerms = description.searchTerms || [];
        this.actOn = description.actOn;
        this.defaultKeys = description.defaultKeys;
        this.hidden = description.hidden || false;
    }
    Object.defineProperty(Action.prototype, "keys", {
        get: function () {
            if (this.remappedKey)
                return [this.remappedKey];
            return this.defaultKeys;
        },
        enumerable: true,
        configurable: true
    });
    Action.prototype.label = function () {
        var key = this.keys[0];
        if (key) {
            return this.name + " (" + key + ")";
        }
        else {
            return this.name;
        }
    };
    return Action;
}());
exports.Action = Action;
var ActionContextServiceClass = /** @class */ (function () {
    function ActionContextServiceClass() {
        this.contextStack = [];
        this.defaultContextStack = [];
        this.contexts = {};
        this.remappingDirty = false;
        // By default, store remappings in localStorage.
        this.onRemappingCallback = function (remapping) {
            localStorage.setItem("phocus-remapping", JSON.stringify(remapping));
        };
    }
    ActionContextServiceClass.prototype.addContext = function (name, context) {
        if (this.contexts[name]) {
            console.warn("Replacing existing context blueprint: ", name);
        }
        this.contexts[name] = context;
    };
    ActionContextServiceClass.prototype.addDefaultContext = function (name, argument, element) {
        this.defaultContextStack.push({
            context: name,
            argument: argument,
            element: element
        });
    };
    ActionContextServiceClass.prototype.removeDefaultContext = function (name, argument, element) {
        this.defaultContextStack = this.defaultContextStack.filter(function (c) {
            return !(c.context === name &&
                c.argument === argument &&
                c.element === element);
        });
    };
    ActionContextServiceClass.prototype.clear = function () {
        this.defaultContextStack = [];
        this.contextStack = [];
        this.contexts = {};
        this.remappingDirty = false;
    };
    ActionContextServiceClass.prototype.getContextStack = function (elt) {
        if (!elt)
            return [];
        var stack = this.getContextStack(elt.parentElement);
        if (elt.dataset.phocusContextName) {
            stack.unshift({
                context: elt.dataset.phocusContextName,
                argument: elt.dataset.phocusContextArgument,
                element: elt
            });
        }
        return stack;
    };
    ActionContextServiceClass.prototype.setContext = function (elt) {
        this.contextStack = this.getContextStack(elt);
    };
    /** Give a user-specific key command for an action. */
    ActionContextServiceClass.prototype.remapAction = function (action, newMapping) {
        action.remappedKey = newMapping;
        this.onRemappingCallback(this.currentRemapping);
    };
    /** Remove a user-specific key command for an action. */
    ActionContextServiceClass.prototype.unremapAction = function (action) {
        action.remappedKey = null;
        this.onRemappingCallback(this.currentRemapping);
    };
    /** Remove a user-specific key command for an action. */
    ActionContextServiceClass.prototype.unmapAction = function (action) {
        action.remappedKey = "None";
        this.onRemappingCallback(this.currentRemapping);
    };
    Object.defineProperty(ActionContextServiceClass.prototype, "currentRemapping", {
        /** Collect all user-remapped actions so that they can be saved */
        get: function () {
            var _this = this;
            var ll = Object.keys(this.contexts).map(function (contextName) {
                var context = _this.contexts[contextName];
                if (!context)
                    return; // TODO assertion, probs
                return Object.keys(context.actions).map(function (actionName) {
                    var action = context.actions[actionName];
                    if (!action.remappedKey)
                        return;
                    return {
                        action: actionName,
                        context: contextName,
                        mapping: action.remappedKey
                    };
                });
            });
            // This is a hot mess of typing. First real "ts is in my way" moment. We've
            // got an array of maybe-remappings, gotta flatten it and remove any
            // undefineds:
            return [].concat
                .apply([], ll)
                .filter(function (x) { return x; });
        },
        enumerable: true,
        configurable: true
    });
    ActionContextServiceClass.prototype.restoreRemapping = function (remapping) {
        var _this = this;
        if (!remapping) {
            remapping = JSON.parse(localStorage.getItem("phocus-remapping") || "null");
            if (!remapping)
                return;
        }
        remapping.map(function (m) {
            var context = _this.contexts[m.context];
            var action = context.actions[m.action];
            _this.remapAction(action, m.mapping);
        });
    };
    ActionContextServiceClass.prototype.onRemapping = function (callback) {
        this.onRemappingCallback = callback;
    };
    Object.defineProperty(ActionContextServiceClass.prototype, "currentStackWithDefaults", {
        get: function () {
            return [].concat(this.contextStack, this.defaultContextStack);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ActionContextServiceClass.prototype, "availableActions", {
        /** Collect actions, along with the appropriate argument, from all
         * contexts in the active stack, smallest context first */
        get: function () {
            return this.actionsInContexts(this.currentStackWithDefaults);
        },
        enumerable: true,
        configurable: true
    });
    ActionContextServiceClass.prototype.actionsInContexts = function (contextStack) {
        var accum = [];
        for (var i = 0; i < contextStack.length; i++) {
            var contextEntry = contextStack[i];
            var context = this.contextFor(contextEntry);
            if (!context) {
                console.error("Unknown action context:", contextEntry.context);
                return;
            }
            var actions = this.actionsFromContext(contextEntry);
            accum = accum.concat(actions);
            if (context.opaque) {
                break;
            }
        }
        return accum;
    };
    ActionContextServiceClass.prototype.actionsFromContext = function (contextEntry) {
        var _this = this;
        var context = this.contextFor(contextEntry);
        var actionsByName = context.actions;
        return Object.keys(actionsByName).map(function (name) {
            return new ActionInContext(actionsByName[name], contextEntry.context, contextEntry.argument, contextEntry.element, _this);
        });
    };
    ActionContextServiceClass.prototype.contextFor = function (contextEntry) {
        return this.contexts[contextEntry.context];
    };
    ActionContextServiceClass.prototype.actionForKeypress = function (key) {
        var availableActions = this.availableActions;
        if (!availableActions)
            return;
        return availableActions.find(function (actionInContext) {
            var action = actionInContext.action;
            return action.keys.indexOf(key) >= 0;
        });
    };
    ActionContextServiceClass.prototype.handleKeypress = function (event) {
        var key = hotkey_1.Hotkey.canonicalKeyFromEvent(event);
        var keyAction = this.actionForKeypress(key);
        if (keyAction) {
            event.preventDefault();
            event.stopPropagation();
            keyAction.act(event);
        }
    };
    ActionContextServiceClass.prototype.actionForName = function (name) {
        var _this = this;
        var contextEntry = this.currentStackWithDefaults.find(function (c) {
            var context = _this.contextFor(c);
            return context && name in context.actions;
        });
        if (!contextEntry)
            return;
        var context = this.contextFor(contextEntry);
        return new ActionInContext(context.actions[name], contextEntry.context, contextEntry.argument, contextEntry.element, this);
    };
    ActionContextServiceClass.prototype.triggerAction = function (name, e) {
        var action = this.actionForName(name);
        if (!action) {
            console.error("No action found for name " + name + ".", this.contextStack);
            return;
        }
        action.act(e);
    };
    return ActionContextServiceClass;
}());
exports.ActionContextServiceClass = ActionContextServiceClass;
exports.ActionContextService = new ActionContextServiceClass();
//# sourceMappingURL=action-context.js.map